import { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo } from 'react';
import { SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    GridCellConfig,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { defaultHRM2120_R01DetailDto, HRM2120_R01DetailDto } from './Dto_HRM2120_R01';
import { useHRM2120_R01CellChange } from './hooks/HRM2120_R01CellChange';
import { useHRM2120_R01Store } from './Store_HRM2120_R01';
import { formatHRM2120_R01EndDt } from './Util_HRM2120_R01';

/** 배정 이력에서 클릭·키보드 편집을 허용할 컬럼이다. */
const EDITABLE_COLUMNS = ['startDt', 'endDt', 'assignType', 'shiftCd', 'rotGroup', 'remark'];
/** 배정 이력 그리드의 컬럼 배치를 저장하는 키이다. */
const COLUMN_STATE_KEY = 'HRM2120_R01-detail-grid-column-state';
/** 저장된 컬럼 배치를 복원할 때 사용할 열 제목이다. */
const HEADER_NAMES = {
    rowIndex: '',
    startDt: '적용시작',
    endDt: '적용종료',
    assignType: '방식',
    shiftCd: '근무조(고정)',
    rotGroup: '교대조',
    remark: '비고',
};

/** 배정 이력 그리드의 참조와 index 행 추가 함수를 받는다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2120_R01DetailDto> | null>; // 이력 그리드
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number) => void; // 클릭한 행 뒤에 신규 배정 추가
}

/** 사원별 근무조 배정 이력을 편집한다. 저장된 시작일은 편집할 수 없다. */
export const DetailTable_HRM2120_R01 = ({ gridRef, handleInsertClick }: Props) => {
    const {
        selectedHRM2120_R01,
        HRM2120_R01DetailList,
        originalHRM2120_R01DetailList,
        updateHRM2120_R01DetailList,
        removeHRM2120_R01DetailList,
        selectedHRM2120_R01DetailList,
        setSelectedHRM2120_R01DetailList,
        deletedHRM2120_R01DetailList,
        workShiftList,
        detailColumnState,
        setDetailColumnState,
        detailScrollTop,
        setDetailScrollTop,
    } = useHRM2120_R01Store();
    /** 앱 초기화에서 조회한 배정 방식·교대조 공통코드를 사용한다. */
    const { shiftAssignList: assignTypeList, rotGroupList } = useCommonCodeStore();
    const { handleCellChange } = useHRM2120_R01CellChange();
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM2120_R01DetailDto>({
        gridRef,
        key: 'valueIndex',
        gridData: HRM2120_R01DetailList,
        defaultGridData: defaultHRM2120_R01DetailDto,
        selectedRow: selectedHRM2120_R01DetailList,
        originalGridData: originalHRM2120_R01DetailList,
        updateRow: updateHRM2120_R01DetailList,
    });
    /** 선택 사원의 삭제되지 않은 배정 행만 편집할 수 있다. */
    const editable = useCallback((row?: HRM2120_R01DetailDto) =>
        !!row && !!selectedHRM2120_R01 && !deletedHRM2120_R01DetailList.includes(String(row.valueIndex)),
    [selectedHRM2120_R01, deletedHRM2120_R01DetailList]);
    /** 신규 배정 행의 -는 저장 전 행을 제거한다. 기존 행 삭제는 액션바를 사용한다. */
    const handleRemoveRow = useCallback((row: HRM2120_R01DetailDto) => {
        if (!row.inserted) return;
        removeHRM2120_R01DetailList(row.valueIndex);
        setSelectedHRM2120_R01DetailList(
            selectedHRM2120_R01DetailList.filter(id => id !== String(row.valueIndex)),
        );
    }, [removeHRM2120_R01DetailList, setSelectedHRM2120_R01DetailList, selectedHRM2120_R01DetailList]);
    /** 공통 index 렌더러로 행 번호와 신규 행의 +, - 버튼을 표시한다. */
    const renderDetailIndexCell = useCallback((params: ICellRendererParams<HRM2120_R01DetailDto>) => {
        return renderIndexCell(
            params,
            innerParams => {
                const flags = getRowStyleFlags(innerParams, {
                    deletedList: deletedHRM2120_R01DetailList,
                    selectedList: selectedHRM2120_R01DetailList,
                    dataList: HRM2120_R01DetailList,
                });
                // 공통 상태 함수는 숫자 ID를 받으므로 선택·삭제 목록은 문자열로 다시 비교한다.
                const rowId = String(innerParams.data?.valueIndex);
                return {
                    ...flags,
                    isDeleted: deletedHRM2120_R01DetailList.includes(rowId),
                    isSelected: selectedHRM2120_R01DetailList.includes(rowId),
                };
            },
            (rowIndex: number, event: React.MouseEvent) => handleInsertClick(event, rowIndex),
            handleRemoveRow,
        );
    }, [
        renderIndexCell,
        deletedHRM2120_R01DetailList,
        selectedHRM2120_R01DetailList,
        HRM2120_R01DetailList,
        handleInsertClick,
        handleRemoveRow,
    ]);
    /** index 열과 배정 이력 컬럼을 정의한다. */
    const baseColumnDefs = useMemo<ColDef<HRM2120_R01DetailDto>[]>(() => [
        ...getDefaultRowIndexColumnConfig(renderDetailIndexCell),
        {
            headerName: '적용시작',
            colId: 'startDt',
            field: 'startDt',
            width: 126,
            editable: params => editable(params.data) && params.data?.inserted === true,
            cellDataType: 'dateString',
            cellEditor: 'agDateStringCellEditor',
            valueFormatter: params => String(params.value || ''),
        },
        {
            headerName: '적용종료',
            colId: 'endDt',
            field: 'endDt',
            width: 126,
            editable: params => editable(params.data),
            cellDataType: 'dateString',
            cellEditor: 'agDateStringCellEditor',
            valueFormatter: params => formatHRM2120_R01EndDt(String(params.value || '')),
        },
        {
            headerName: '방식',
            colId: 'assignType',
            field: 'assignType',
            width: 110,
            editable: params => editable(params.data),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: assignTypeList.map(code => code.minorCd) },
            valueFormatter: params => assignTypeList.find(code => code.minorCd === params.value)?.codeNm
                || String(params.value || ''),
        },
        {
            headerName: '근무조(고정)',
            colId: 'shiftCd',
            field: 'shiftCd',
            width: 110,
            editable: params => editable(params.data),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: workShiftList.map(shift => shift.shiftCd) },
            valueFormatter: params => workShiftList.find(shift => shift.shiftCd === params.value)?.shiftNm
                || String(params.value || ''),
        },
        {
            headerName: '교대조',
            colId: 'rotGroup',
            field: 'rotGroup',
            width: 80,
            editable: params => editable(params.data) && params.data?.assignType === 'ROT',
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: rotGroupList.map(code => code.minorCd) },
            valueFormatter: params => rotGroupList.find(code => code.minorCd === params.value)?.codeNm
                || String(params.value || ''),
        },
        {
            headerName: '비고',
            colId: 'remark',
            field: 'remark',
            width: 200,
            editable: params => editable(params.data),
            cellEditorParams: { maxLength: 1000 },
        },
    ], [editable, workShiftList, assignTypeList, rotGroupList, renderDetailIndexCell]);
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    /** 저장된 배정 이력 그리드의 컬럼 배치를 공통 함수로 복원한다. */
    const columnDefs = useMemo(() => {
        if (detailColumnState === null) return baseColumnDefs;
        const restoredColumns = GridCustomColumnDefs<HRM2120_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames: HEADER_NAMES,
        });
        /** 기존 저장 컬럼 설정에 index가 없어도 맨 앞에 새 index 열을 표시한다. */
        return restoredColumns.some(column => 'colId' in column && column.colId === 'rowIndex')
            ? restoredColumns : [baseColumnDefs[0], ...restoredColumns];
    }, [baseColumnDefs, detailColumnState]);
    /** 기존 적용시작만 잠그고 신규·수정·삭제 상태는 공통 셀 규칙으로 표시한다. */
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [],
        insertedDisabledCells: [],
        commonDisabledCells: ['startDt'],
        pointerCells: ['endDt', 'assignType', 'shiftCd', 'rotGroup', 'remark'],
        insertedPointerCells: ['startDt'],
        commonPointerCells: [],
        alignLeftCells: ['remark'],
    }), []);
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM2120_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedHRM2120_R01DetailList.map(Number),
            deletedList: deletedHRM2120_R01DetailList.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedHRM2120_R01DetailList, deletedHRM2120_R01DetailList, cellCssColumns]);
    /** 공통 이벤트가 편집 가능한 배정 컬럼 사이의 키보드 이동을 처리한다. */
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM2120_R01DetailList,
        editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });
    const cellClickActions = useMemo<CustomCellClickAction<HRM2120_R01DetailDto>[]>(() => [{
        validate: (field, row) => EDITABLE_COLUMNS.includes(field) && editable(row)
            && (field !== 'startDt' || row.inserted)
            && (field !== 'rotGroup' || row.assignType === 'ROT'),
        action: event => event.api.startEditingCell({
            rowIndex: event.rowIndex!,
            colKey: event.colDef.field!,
        }),
    }], [editable]);
    const handleCellClick = CreateCellClickHandler(HRM2120_R01DetailList, 'valueIndex', cellClickActions);

    /** 단일 클릭은 선택을 바꾸고 Ctrl/Command 클릭은 현재 선택 목록을 토글한다. */
    const handleRowClick = useCallback((event: RowClickedEvent<HRM2120_R01DetailDto>) => {
        if (event.node.rowPinned || !event.data) return;

        const rowId = String(event.data.valueIndex);
        const mouse = event.event as MouseEvent | undefined;
        const isMultiSelect = mouse?.ctrlKey || mouse?.metaKey;
        if (!isMultiSelect) {
            setSelectedHRM2120_R01DetailList([rowId]);
            return;
        }

        const nextSelected = selectedHRM2120_R01DetailList.includes(rowId)
            ? selectedHRM2120_R01DetailList.filter(id => id !== rowId)
            : [...selectedHRM2120_R01DetailList, rowId];
        setSelectedHRM2120_R01DetailList(nextSelected);
    }, [selectedHRM2120_R01DetailList, setSelectedHRM2120_R01DetailList]);

    /** 하단 고정 행의 적용시작 칸에 배정 이력 건수를 표시한다. */
    const pinnedBottomRow = useMemo(() => [{
        startDt: `${HRM2120_R01DetailList.length.toLocaleString()}건`,
    }], [HRM2120_R01DetailList.length]);

    return (
        <section className="flex flex-col h-full min-h-0">
            <GridContextWrapper<HRM2120_R01DetailDto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                initSelected={() => setSelectedHRM2120_R01DetailList([])}
                allSelected={() => setSelectedHRM2120_R01DetailList(HRM2120_R01DetailList
                    .filter(row => !deletedHRM2120_R01DetailList.includes(String(row.valueIndex)))
                    .map(row => String(row.valueIndex)))}
                defaultRowData={defaultHRM2120_R01DetailDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setDetailColumnState}
                localStorageKey={COLUMN_STATE_KEY}
            >
                {/* 배정 이력 제목 오른쪽에 선택 사원이 있을 때만 사번과 이름을 표시한다. */}
                <SmallTitleWithSubSpan title="배정 이력" subTitle={selectedHRM2120_R01
                    ? `${selectedHRM2120_R01.empNo} ${selectedHRM2120_R01.empNm}`
                    : ''} />
                <Grid_Excel<HRM2120_R01DetailDto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM2120_R01DetailDto>({
                        idKey: 'valueIndex',
                        selectedList: selectedHRM2120_R01DetailList.map(Number),
                        deletedList: deletedHRM2120_R01DetailList.map(Number),
                    })}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                    onCellValueChanged={handleCellChange}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setDetailColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={detailScrollTop}
                    setScrollTop={setDetailScrollTop}
                />
            </GridContextWrapper>
        </section>
    );
};
